import { createSelector } from 'reselect';
import { selectActiveComponentIndex } from './search.selector';
import { orderByDesc, orderByAsc } from '../../utils/filter.utils';
import {
  convertAirDataToSegment,
  filterByMatrix,
  mapAirportList,
  convertAirDataToSegmentCloud,
  filterByMatrixCloud,
  removeDuplicatesFromArray,
} from '../../utils/air.util';
import { selectCompanyCurrency, selectCompanyCurrencyCulture } from './auth.selector';
import moment from 'moment';
import { MULTIPLE_AIRLINE } from '../../constants/Enums';

export const selectAirFeatureState = (state) => state.air;

export const selectAirComponents = createSelector(
  selectAirFeatureState,
  (state) => state && state.components
);

export const selectedFlightComponents = createSelector(selectAirComponents, (state) => {
  const keys = Object.keys(state);
  const selectedFlights = [];
  keys.map((res) => {
    selectedFlights.push(state[res].selectedAir);
  });
  return state && selectedFlights;
});

export const selectActiveAirComponent = createSelector(
  selectAirComponents,
  selectActiveComponentIndex,
  (state, displayIndex) => state && state[displayIndex]
);

export const selectAirSearchResults = createSelector(
  selectActiveAirComponent,
  (state) => state?.searchResults
);
export const selectAirAutoExchangeViewModel = createSelector(
  selectAirSearchResults,
  (state) => state && state.AirAutoExchangeViewModel
);

export const selectSelectedAir = createSelector(
  selectActiveAirComponent,
  (state) => state?.selectedAir
);

export const selectAirResponses = createSelector(
  selectAirSearchResults,
  (state) => state && state.AirResponses
);

export const selectOrderByAir = createSelector(selectActiveAirComponent, (state) => state?.orderBy);

export const selectOrderByReverseAir = createSelector(
  selectActiveAirComponent,
  (state) => state?.reverse
);

export const selectFilterByAir = createSelector(
  selectActiveAirComponent,
  (state) => state?.filterBy
);

export const selectFilterByAirBrand = createSelector(
  selectActiveAirComponent,
  (state) => state?.filterByBrand
);

export const selectAirFilters = createSelector(
  selectActiveAirComponent,
  (state) => state?.filterByDetails
);

export const selectAirBrands = createSelector(
  selectActiveAirComponent,
  (state) => state?.filterBrands
);

export const selectAirResponseSummary = createSelector(
  selectAirSearchResults,
  (state) => state?.AirResponseSummary
);
export const selectLowestFareForLeg1 = createSelector(
  selectAirComponents,
  (state) => state[1]?.searchResults?.AirResponseSummary?.LowestPrice
);

export const selectCloudLowestFareForLeg1 = createSelector(
  selectAirComponents,
  (state) => state[1]?.searchResults?.lowestPrice
);

export const selectAirMapCoordinates = createSelector(selectAirResponseSummary, (state) => {
  if (!state) {
    return state;
  }
  return state.AirMapData.reduce((acum, segment) => {
    acum.push(
      {
        latitude: Number(segment.DepartLattitude),
        longitude: Number(segment.DepartLongitude),
      },
      {
        latitude: Number(segment.Lattitude),
        longitude: Number(segment.Longitude),
      },
      {
        latitude: Number(segment.ArrivalLattitude),
        longitude: Number(segment.ArrivalLongitude),
      }
    );
    return acum;
  }, []);
});

export const selectDepartureAirportList = createSelector(selectAirResponseSummary, (state) =>
  mapAirportList(state?.DepartureAirportList)
);

export const selectArrivalAirportList = createSelector(selectAirResponseSummary, (state) =>
  mapAirportList(state?.ArrivalAirportList)
);

//todoBACK this assumes that round trip flight will always be displayIndex 1
export const selectSelectedAirFirstResponseId = createSelector(
  selectAirComponents,
  (state) => state && state[1] && state[1].selectedAir?.ResponseID
);

export const selectSelectedAirPreviousResponseId = createSelector(
  selectAirComponents,
  selectActiveComponentIndex,
  (state, displayIndex) =>
    state && state[displayIndex - 1] && state[displayIndex - 1].selectedAir?.ResponseID
);
export const selectSelectedAirPreviousResponseIdCloud = createSelector(
  selectAirComponents,
  selectActiveComponentIndex,
  (state, displayIndex) =>
    state && state[displayIndex - 1] && state[displayIndex - 1].selectedAir?.id
);

export const selectSelectedMultibrandKey = createSelector(
  selectAirComponents,
  (state) => state && state[1] && state[1].newMultiBrandResponseKey
);
export const selectInternationalTravel = createSelector(
  selectAirComponents,
  (state) => state && state[1] && state[1].searchResults?.IsInternationalTravel
);
export const selectResultsFilteredByBrand = createSelector(
  selectAirResponses,
  selectFilterByAirBrand,
  (state, airBrand) => {
    if (!state || !airBrand || airBrand === 'lowest') {
      return state;
    }

    return state
      .filter(({ MultiBrands }) =>
        MultiBrands.some(({ LegBrandName }) => LegBrandName.toLowerCase() === airBrand)
      )
      .map((flight) => {
        const index = flight.MultiBrands.findIndex(
          ({ LegBrandName }) => LegBrandName.toLowerCase() === airBrand
        );

        return {
          ...flight,
          TotalAmount: flight.MultiBrands[index].TotalFare,
          selectedBrand: index,
        };
      });
  }
);

export const selectResultsFilteredByDetails = createSelector(
  selectResultsFilteredByBrand,
  selectAirFilters,
  (state, filterByDetails) => {
    if (!state || !filterByDetails) {
      return state;
    }

    const { stopAndVendor, departureAirport, arrivalAirport } = filterByDetails;

    let orderedList = [];

    if (stopAndVendor && Object.keys(stopAndVendor).length > 0) {
      orderedList = state.filter(({ AirLegs }) => {
        const length = AirLegs[0].AirSegments.length;
        const stops = length > 3 ? 3 : length;
        const code = AirLegs[0].AirlineCode;

        return stopAndVendor[stops]?.items[code]?.checked;
      });
    }
    if (departureAirport) {
      orderedList = orderedList.filter(({ AirLegs }) => {
        const airLeg = AirLegs[0];

        return departureAirport.departure.items[airLeg.DepartureAirportCode].checked;
      });
    }

    if (arrivalAirport) {
      orderedList = orderedList.filter(({ AirLegs }) => {
        const airLeg = AirLegs[0];

        return arrivalAirport.arrival.items[airLeg.ArrivalAirportCode].checked;
      });
    }

    return orderedList;
  }
);

export const selectConvertedAirMatrixData = createSelector(
  selectResultsFilteredByDetails,
  selectCompanyCurrency,
  selectCompanyCurrencyCulture,
  (state, currency, currencyCulture) => convertAirDataToSegment(state, currency, currencyCulture)
);

export const selectResultsFilteredByMatrix = createSelector(
  selectResultsFilteredByDetails,
  selectFilterByAir,
  (state, filterBy) => filterByMatrix(state, filterBy)
);

export const selectResultsFilteredAndOrdered = createSelector(
  selectResultsFilteredByMatrix,
  selectOrderByAir,
  selectOrderByReverseAir,
  (state, orderBy, reverse) => {
    if (!state || !orderBy) {
      return state;
    }

    if (orderBy === 'TotalAmount') {
      if (reverse) {
        state.slice().sort((a, b) => orderByAsc(a[orderBy], b[orderBy]));
      } else {
        state.slice().sort((a, b) => orderByDesc(a[orderBy], b[orderBy]));
      }
      state.map((item) => {
        if (item.MultiBrands && item.MultiBrands.length >= 2 && item.MultiBrands[0].IsSuppressed) {
          const unblockedfareIndex = item.MultiBrands.findIndex((fare) => !fare.IsSuppressed);
          // Swap the 1st blocked fare with unblocked fare
          [item.MultiBrands[unblockedfareIndex], item.MultiBrands[0]] = [
            item.MultiBrands[0],
            item.MultiBrands[unblockedfareIndex],
          ];
          return item;
        }
      });
      return state;
    } else {
      if (reverse) {
        return state
          .slice()
          .sort((a, b) => orderByAsc(a.AirLegs[0][orderBy], b.AirLegs[0][orderBy]));
      } else {
        return state
          .slice()
          .sort((a, b) => orderByDesc(a.AirLegs[0][orderBy], b.AirLegs[0][orderBy]));
      }
    }
  }
);

// Cloud logic starts here:

export const selectAirCloudResponses = createSelector(
  selectAirSearchResults,
  (state) => state && state.options
);
export const selectAirCloudMapCoordinates = createSelector(selectAirSearchResults, (state) => {
  if (!state) {
    return state;
  }
  return state.airMapDetails?.reduce((mapData, segment) => {
    const airMapDet = {};
    const airmapcityName = state.airports.filter((c) => c.code == segment.airportCode);
    if (airmapcityName != undefined && airmapcityName.length > 0) {
      airMapDet.airportCityName = airmapcityName[0].CityName;
      airMapDet.Lattitude = airmapcityName[0].lat;
      airMapDet.Longitude = airmapcityName[0].long;
    } else {
      airMapDet.airportCityName = '';
    }
    const departAirport = state.airports.filter((c) => c.code == segment.departAirportCode);
    if (departAirport != undefined && departAirport.length > 0) {
      airMapDet.DepartLattitude = departAirport[0].lat;
      airMapDet.DepartLongitude = departAirport[0].long;
    } else {
      airMapDet.DepartLattitude = '';
      airMapDet.DepartLongitude = '';
    }
    const arrivalAirport = state.airports.filter((c) => c.code == segment.arrivalAirportCode);
    if (arrivalAirport != undefined && arrivalAirport.length > 0) {
      airMapDet.ArrivalLattitude = arrivalAirport[0].lat;
      airMapDet.ArrivalLongitude = arrivalAirport[0].long;
    } else {
      airMapDet.DepartLattitude = '';
      airMapDet.DepartLongitude = '';
    }
    mapData.push(
      {
        latitude: Number(airMapDet.DepartLattitude),
        longitude: Number(airMapDet.DepartLongitude),
      },
      {
        latitude: Number(airMapDet.Lattitude),
        longitude: Number(airMapDet.Longitude),
      },
      {
        latitude: Number(airMapDet.ArrivalLattitude),
        longitude: Number(airMapDet.ArrivalLongitude),
      }
    );
    return mapData;
  }, []);
});
export const selectDepartureAirportListCloud = createSelector(selectAirSearchResults, (state) => {
  const optionsData = state?.options?.map((data) => {
    const flights = data?.legs[0]?.flights;
    const depAirportCode = flights[0]?.departure;
    const depAirport = state.airports?.filter((airport) => airport?.code === depAirportCode);
    const depData = {
      airportCode: depAirport[0]?.code,
      airportName: depAirport[0]?.CityName,
      airportCityName: depAirport[0]?.CityName,
      airportLowestPrice: 0,
      airportLowestPoints: 0,
      price: 0,
      points: 0,
    };
    return depData;
  });
  const depAir = optionsData?.flat()[0];
  return optionsData && mapAirportList([depAir]);
});
export const selectArrivalAirportListCloud = createSelector(selectAirSearchResults, (state) => {
  const optionsData = state?.options?.map((data) => {
    const flights = data.legs[0].flights;
    const arrAirportCode = flights[flights?.length - 1]?.arrival;
    const arrAirport = state.airports?.filter((airport) => airport.code === arrAirportCode);
    const arrData = {
      airportCode: arrAirport[0]?.code,
      airportName: arrAirport[0]?.CityName,
      airportCityName: arrAirport[0]?.CityName,
      airportLowestPrice: 0,
      airportLowestPoints: 0,
      price: 0,
      points: 0,
    };
    return arrData;
  });
  const arrAir = optionsData?.flat()[0];
  return optionsData && mapAirportList([arrAir]);
});
export const selectCloudResultsFilteredByBrand = createSelector(
  selectAirCloudResponses,
  selectFilterByAirBrand,
  (state, airBrand) => {
    if (!state || !airBrand || airBrand === 'lowest') {
      return state;
    }

    return state
      .filter(({ fares }) => fares.some(({ brand }) => brand.toLowerCase() === airBrand))
      .map((flight) => {
        const index = flight.fares.findIndex(({ brand }) => brand.toLowerCase() === airBrand);

        return {
          ...flight,
          TotalAmount: flight.fares[index].total,
          selectedBrand: index,
        };
      });
  }
);
export const selectCloudResultsFilteredByDetails = createSelector(
  selectCloudResultsFilteredByBrand,
  selectAirFilters,
  (state, filterByDetails) => {
    if (!state || !filterByDetails) {
      return state;
    }

    const { stopAndVendor, departureAirport, arrivalAirport } = filterByDetails;

    let orderedList = [];

    if (stopAndVendor && Object.keys(stopAndVendor).length > 0) {
      orderedList = state.filter((data) => {
        const stops = data?.legs[0]?.flights?.length;
        const code = data?.legs[0]?.isMultipleAirline
          ? MULTIPLE_AIRLINE
          : data?.legs[0]?.flights[0]?.marketting;
        return stopAndVendor[stops]?.items[code]?.checked;
      });
    }

    if (departureAirport) {
      orderedList = orderedList.filter(({ AirLegs }) => {
        const airLeg = AirLegs[0];

        return departureAirport.departure.items[airLeg.DepartureAirportCode].checked;
      });
    }

    if (arrivalAirport) {
      orderedList = orderedList.filter(({ AirLegs }) => {
        const airLeg = AirLegs[0];

        return arrivalAirport.arrival.items[airLeg.ArrivalAirportCode].checked;
      });
    }

    return orderedList;
  }
);
export const selectConvertedAirMatrixDataCloud = createSelector(
  selectCloudResultsFilteredByDetails,
  selectCompanyCurrency,
  selectCompanyCurrencyCulture,
  (state, currency, currencyCulture) =>
    convertAirDataToSegmentCloud(state, currency, currencyCulture)
);
export const selectCloudResultsFilteredByMatrix = createSelector(
  selectCloudResultsFilteredByDetails,
  selectFilterByAir,
  (state, filterBy) => filterByMatrixCloud(state, filterBy)
);
export const selectCloudResultsFilteredAndOrdered = createSelector(
  selectCloudResultsFilteredByMatrix,
  selectOrderByAir,
  selectOrderByReverseAir,
  (state, orderBy, reverse) => {
    if (!state || !orderBy) {
      return state;
    }
    //TODO: CHECK THIS ONE OUT ONCE EVERYTHING IS DONE:
    if (orderBy === 'TotalAmount') {
      if (reverse) {
        state?.slice()?.sort((a, b) => orderByAsc(a?.fares[0]?.total, b?.fares[0]?.total));
      } else {
        state?.slice()?.sort((a, b) => orderByDesc(a?.fares[0]?.total, b?.fares[0]?.total));
      }

      state?.map((item) => {
        if (item?.fares && item?.fares?.length >= 2 && item?.fares[0]?.isSuppressed) {
          // Swap the 1st blocked fare with unblocked fare
          const unblockedfareIndex = item?.fares?.findIndex((fare) => !fare?.isSuppressed);
          [item.fares[unblockedfareIndex], item.fares[0]] = [
            item.fares[0],
            item.fares[unblockedfareIndex],
          ];
        }
        return item;
      });

      return state;
    } else {
      if (reverse) {
        if (orderBy === 'FilterDepart') {
          return state
            .slice()
            .sort((a, b) =>
              orderByAsc(
                parseInt(moment(a.legs[0].flights[0].departureTime, 'hh:mm').format('HHmm'), 10),
                parseInt(moment(b.legs[0].flights[0].departureTime, 'hh:mm').format('HHmm'), 10)
              )
            );
        } else if (orderBy === 'FilterArrival') {
          return state
            .slice()
            .sort((a, b) =>
              orderByAsc(
                parseInt(moment(a.legs[0].flights[0].arrivalTime, 'hh:mm').format('HHmm'), 10),
                parseInt(moment(b.legs[0].flights[0].arrivalTime, 'hh:mm').format('HHmm'), 10)
              )
            );
        } else if (orderBy === 'FilterDuration') {
          return state
            .slice()
            .sort((a, b) => orderByAsc(a.legs[0].totalDuration, b.legs[0].totalDuration));
        }
      } else {
        if (orderBy === 'FilterDepart') {
          return state
            .slice()
            .sort((a, b) =>
              orderByDesc(
                parseInt(moment(a.legs[0].flights[0].departureTime, 'hh:mm').format('HHmm'), 10),
                parseInt(moment(b.legs[0].flights[0].departureTime, 'hh:mm').format('HHmm'), 10)
              )
            );
        } else if (orderBy === 'FilterArrival') {
          return state
            .slice()
            .sort((a, b) =>
              orderByDesc(
                parseInt(moment(a.legs[0].flights[0].arrivalTime, 'hh:mm').format('HHmm'), 10),
                parseInt(moment(b.legs[0].flights[0].arrivalTime, 'hh:mm').format('HHmm'), 10)
              )
            );
        } else if (orderBy === 'FilterDuration') {
          return state
            .slice()
            .sort((a, b) => orderByDesc(a.legs[0].totalDuration, b.legs[0].totalDuration));
        }
      }
    }
  }
);

export const selectAirlinesCloud = createSelector(selectAirComponents, (state) => {
  const allAirlines = Object.keys(state)
    ?.map((key) => state[key]['searchResults']['airlines'])
    ?.flat();
  const airlines = removeDuplicatesFromArray(allAirlines, (airline) => airline?.code);
  return airlines;
});

export const selectAirportsCloud = createSelector(selectAirComponents, (state) => {
  const allAirports = Object.keys(state)
    ?.map((key) => state[key]['searchResults']['airports'])
    ?.flat();
  const airports = removeDuplicatesFromArray(allAirports, (airport) => airport?.code);
  return airports;
});
export const selectAircraftsCloud = createSelector(selectAirComponents, (state) => {
  const allAircrafts = Object.keys(state)
    ?.map((key) => state[key]['searchResults']['airCrafts'])
    ?.flat();
  const aircrafts = removeDuplicatesFromArray(allAircrafts, (aircraft) => aircraft?.code);
  return aircrafts;
});
export const selectAirMapDetailsCloud = createSelector(
  selectAirSearchResults,
  (state) => state?.airMapDetails
);
